import React, { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { MessageSquarePlus, X, Loader2, Camera, CheckCircle2, Bug, Wrench, Sparkles, Palette } from "lucide-react";

const TYPES = [
  { key: "bug", label: "Bug or something not working", icon: Bug },
  { key: "tweak", label: "Small tweak or adjustment", icon: Wrench },
  { key: "improvement", label: "Improvement or new feature", icon: Sparkles },
  { key: "design", label: "Design or usability feedback", icon: Palette },
];

function recordRef() {
  const m = window.location.pathname.match(/\/(tasks|runs|orders|projects|quotes|contacts|products)\/([^/]+)/);
  return m ? `${m[1]}:${m[2]}` : "";
}

export function FeedbackNavButton({ onClick }) {
  const { user, can, canAny } = useAuth();
  if (!user || user === true || !(user.is_super || can("dashboard.view") || canAny(["feedback.submit", "feedback.manage"]))) return null;
  return (
    <button type="button" data-testid="sidebar-nav-feedback" title="Send feedback"
      onClick={() => { onClick?.(); window.dispatchEvent(new Event("open-feedback")); }}
      className="w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium text-slate-600 hover:bg-slate-100 hover:text-slate-900 transition-colors duration-150">
      <MessageSquarePlus className="w-[18px] h-[18px]" strokeWidth={2} />
      <span className="flex-1 text-left">Feedback</span>
    </button>
  );
}

export default function FeedbackWidget() {
  const { user, can, canAny } = useAuth();
  const [open, setOpen] = useState(false);
  const [type, setType] = useState("bug");
  const [title, setTitle] = useState("");
  const [desc, setDesc] = useState("");
  const [priority, setPriority] = useState("medium");
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);

  useEffect(() => {
    const h = () => setOpen(true);
    window.addEventListener("open-feedback", h);
    return () => window.removeEventListener("open-feedback", h);
  }, []);

  const canSubmit = !!user && user !== true && (user.is_super || can("dashboard.view") || canAny(["feedback.submit", "feedback.manage"]));
  if (!canSubmit) return null;

  const reset = () => { setType("bug"); setTitle(""); setDesc(""); setPriority("medium"); setFile(null); setDone(null); };
  const close = () => { setOpen(false); setTimeout(reset, 200); };

  const submit = async () => {
    if (!title.trim()) return;
    setBusy(true);
    try {
      const body = { type, title: title.trim(), description: desc, priority,
        page_url: window.location.pathname + window.location.search, record_ref: recordRef() };
      const r = await api.post("/feedback", body);
      const t = r.data.ticket;
      if (file) {
        const fd = new FormData(); fd.append("file", file);
        try { await api.post(`/feedback/${t.id}/attachments`, fd); }
        catch { const { toast } = await import("sonner"); toast.warning("Feedback saved, but the screenshot upload failed."); }
      }
      setDone(t.ref);
    } catch (e) {
      const { toast } = await import("sonner");
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Could not submit feedback");
    } finally { setBusy(false); }
  };

  return (
    <>
      {open && (
        <div className="fixed bottom-5 right-5 z-[70] w-[calc(100vw-2.5rem)] max-w-md" data-testid="feedback-panel">
          <div className="bg-white border border-slate-200 rounded-2xl shadow-2xl overflow-hidden">
            <div className="flex items-start justify-between px-5 pt-4 pb-3">
              <div>
                <div className="flex items-center gap-2">
                  <h2 className="text-base font-bold text-slate-900">Feedback &amp; Improvements</h2>
                  <span className="text-[11px] font-semibold rounded-full bg-indigo-50 text-indigo-600 px-2 py-0.5">Tester mode</span>
                </div>
                <p className="text-xs text-slate-500 mt-0.5">Help us improve TedOps AI. Report an issue or suggest a change.</p>
              </div>
              <button onClick={close} data-testid="feedback-close" className="p-1 text-slate-400 hover:text-slate-700"><X className="w-5 h-5" /></button>
            </div>

            {done ? (
              <div className="px-5 py-8 text-center" data-testid="feedback-success">
                <CheckCircle2 className="w-10 h-10 text-emerald-500 mx-auto mb-2" />
                <p className="text-sm font-semibold text-slate-900">Thanks — feedback submitted</p>
                <p className="text-xs text-slate-500 mt-0.5">Reference <span className="font-mono">{done}</span>. Track it under Docs → Feedback &amp; Bugs.</p>
                <Button variant="outline" className="mt-4 h-9" onClick={reset} data-testid="feedback-another">Submit another</Button>
              </div>
            ) : (
              <div className="px-5 pb-5 space-y-3 max-h-[70vh] overflow-y-auto">
                <div className="rounded-xl border border-slate-200 p-3">
                  <p className="text-xs font-semibold text-slate-500 mb-2">What would you like to report?</p>
                  <div className="space-y-1.5">
                    {TYPES.map((t) => (
                      <button key={t.key} onClick={() => setType(t.key)} data-testid={`feedback-type-${t.key}`}
                        className={`w-full flex items-center gap-2.5 rounded-lg border px-3 py-2 text-left text-sm transition-colors ${type === t.key ? "border-indigo-400 bg-indigo-50/60 text-slate-900" : "border-transparent hover:bg-slate-50 text-slate-700"}`}>
                        <span className={`w-4 h-4 rounded-full border-2 flex items-center justify-center ${type === t.key ? "border-indigo-600" : "border-slate-300"}`}>
                          {type === t.key && <span className="w-2 h-2 rounded-full bg-indigo-600" />}
                        </span>
                        {t.label}
                      </button>
                    ))}
                  </div>
                </div>

                <div>
                  <label className="text-xs font-semibold text-slate-500">Title</label>
                  <Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Short summary" className="mt-1 h-9" data-testid="feedback-title" />
                </div>

                <div>
                  <label className="text-xs font-semibold text-slate-500">Describe your feedback</label>
                  <textarea value={desc} onChange={(e) => setDesc(e.target.value)} rows={4} data-testid="feedback-desc"
                    placeholder="What happened, or what would you like changed?"
                    className="mt-1 w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-200" />
                </div>

                <div className="flex items-center gap-3">
                  <label className="text-xs font-semibold text-slate-500">Priority</label>
                  <div className="flex gap-1.5">
                    {["low", "medium", "high"].map((p) => (
                      <button key={p} onClick={() => setPriority(p)} data-testid={`feedback-priority-${p}`}
                        className={`text-xs font-semibold rounded-md px-2.5 py-1 border capitalize transition-colors ${priority === p ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-600 border-slate-200 hover:border-slate-400"}`}>{p}</button>
                    ))}
                  </div>
                </div>

                <label className="flex items-center gap-2 text-sm text-slate-600 cursor-pointer" data-testid="feedback-attach-label">
                  <Camera className="w-4 h-4" /> {file ? file.name : "Attach a screenshot"}
                  <input type="file" accept="image/*" className="hidden" data-testid="feedback-attach"
                    onChange={(e) => setFile(e.target.files?.[0] || null)} />
                </label>

                <Button onClick={submit} disabled={busy || !title.trim()} data-testid="feedback-submit"
                  className="w-full h-10 bg-indigo-600 hover:bg-indigo-700 text-white gap-2">
                  {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : null} Submit feedback
                </Button>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
}
